import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../hooks/redux';
import { fetchProjectById, updateProject } from '../store/slices/projectsSlice';
import { api } from '../services/api';
import {
  ProjectHeader,
  ProjectHeroCarousel,
  ProjectTabs,
  ProjectMilestones,
  ProjectGoals,
} from '../components/Projects';
import ProjectFilesList from '../components/Projects/ProjectFilesList';
import VotingSessionScheduler from '../components/Voting/VotingSessionScheduler';
import Button from '../components/UI/Button';
import VotingSessionCard from '../components/Voting/VotingSessionCard';
import CommentList from '../components/Comments/CommentList';
import { ChatRoom } from '../components/Chat';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import {
  InlineEditTextarea,
  InlineEditLocation,
  InlineEditTags,
} from '../components/UI/InlineEdit';

const ProjectDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { items, isLoading } = useAppSelector((state) => state.projects);
  const { user } = useAppSelector((state) => state.auth);
  
  // State to control active tab - MUST be called before any early returns
  const [activeTab, setActiveTab] = useState<string>('overview');
  const [projectFiles, setProjectFiles] = useState<any[]>([]);
  const { accessToken } = useAppSelector((state) => state.auth);

  useEffect(() => {
    if (id) {
      dispatch(fetchProjectById(id));
    }
  }, [dispatch, id]);

  // Look up project by id or _id (backend returns _id, but URL might have id)
  const project = id ? (items[id] || Object.values(items).find((p: any) => p._id === id || p.id === id)) : null;

  // Fetch project files separately
  useEffect(() => {
    const fetchFiles = async () => {
      if (!id || !project) return;
      try {
        const files = await api.projectFiles.getProjectFiles(id, false, accessToken || undefined);
        setProjectFiles(files);
      } catch (error) {
        console.error('Error fetching project files:', error);
        setProjectFiles([]);
      }
    };

    if (project) {
      fetchFiles();
    }
  }, [id, project, accessToken]);

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <LoadingSpinner size="lg" text="Loading project..." />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="container mx-auto px-4 py-8">
        <p className="text-[var(--color-subtext-color)]">Project not found</p>
      </div>
    );
  }

  const isOwner = user?.id === project.ownerId?._id || user?.id === project.ownerId;

  const images = projectFiles.filter((f: any) => f.type === 'image' && !f.deletedAt);

  // Find active voting session
  const findActiveVotingSession = () => {
    if (!project.votingSessions || project.votingSessions.length === 0) {
      return null;
    }

    const now = new Date();
    const activeSession = project.votingSessions.find((session: any) => {
      const startDate = new Date(session.startDate);
      const endDate = new Date(session.endDate);
      return session.status === 'active' && now >= startDate && now <= endDate;
    });

    return activeSession || null;
  };

  // Find nearest scheduled voting session
  const findNearestScheduledSession = () => {
    if (!project.votingSessions || project.votingSessions.length === 0) {
      return null;
    }

    const now = new Date();
    const scheduledSessions = project.votingSessions.filter((session: any) => {
      const startDate = new Date(session.startDate);
      return session.status === 'scheduled' && startDate > now;
    });

    if (scheduledSessions.length === 0) {
      return null;
    }

    // Sort by startDate and return the nearest one
    scheduledSessions.sort((a: any, b: any) => {
      return new Date(a.startDate).getTime() - new Date(b.startDate).getTime();
    });

    return scheduledSessions[0];
  };

  const activeVotingSession = findActiveVotingSession();
  const nearestVotingSession = findNearestScheduledSession();

  // Scroll to voting tab when "Vote Now" is clicked
  const handleVoteNowClick = () => {
    // Set active tab to voting
    setActiveTab('voting');
    
    // Scroll to tabs section
    setTimeout(() => {
      const tabsSection = document.querySelector('[data-tabs-section]');
      if (tabsSection) {
        tabsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 500);
  };

  const handleFieldUpdate = async (field: string, value: any) => {
    if (!id) return;
    const projectId = project._id || project.id || id;
    try {
      const result = await dispatch(updateProject({ id: projectId, data: { [field]: value } }));
      if (updateProject.fulfilled.match(result)) {
        // Project is automatically updated in Redux store via the fulfilled case
        console.log('Project updated successfully:', field, value);
      } else {
        console.error('Failed to update project:', result);
      }
    } catch (error) {
      console.error('Error updating project:', error);
    }
  };

  // Voting Tab Content Component
  const VotingTabContent: React.FC<{
    project: any;
    isOwner: boolean;
    projectId: string;
  }> = ({ project, isOwner, projectId }) => {
    const [showScheduler, setShowScheduler] = useState(false);
    const { accessToken } = useAppSelector((state) => state.auth);

    const handleUpdateSession = async (sessionId: string, updates: any) => {
      if (!accessToken) {
        throw new Error('Authentication required');
      }
      await api.voting.updateSession(projectId, sessionId, updates, accessToken);
      // Refresh project data
      await dispatch(fetchProjectById(projectId));
    };

    return (
      <div className="space-y-4">
        {isOwner && (
          <div className="flex justify-end">
            {!showScheduler ? (
              <Button
                type="button"
                size="sm"
                onClick={() => setShowScheduler(true)}
              >
                Schedule Voting Session
              </Button>
            ) : (
              <div className="w-full border border-[var(--color-neutral-border)] rounded-lg p-4 bg-[var(--color-neutral-50)]">
                <VotingSessionScheduler
                  projectId={projectId}
                  onSessionCreated={() => {
                    setShowScheduler(false);
                  }}
                  onCancel={() => setShowScheduler(false)}
                />
              </div>
            )}
          </div>
        )}

        {project.votingSessions && project.votingSessions.length > 0 ? (
          project.votingSessions.map((session: any) => (
            <VotingSessionCard 
              key={session.sessionId} 
              session={session} 
              projectId={projectId}
              isOwner={isOwner}
              onUpdate={handleUpdateSession}
            />
          ))
        ) : (
          <p className="text-[var(--color-subtext-color)]">No voting sessions</p>
        )}
      </div>
    );
  };

  const tabs = [
    {
      id: 'overview',
      label: 'Overview',
      content: (
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold mb-2">Description</h3>
            <InlineEditTextarea
              value={project.description}
              onSave={(value) => handleFieldUpdate('description', value)}
              disabled={!isOwner}
              placeholder="Project description"
              rows={6}
            />
          </div>
          {project.geographicRegion && (
            <div>
              <h3 className="text-lg font-semibold mb-2">Location</h3>
              <InlineEditLocation
                value={project.geographicRegion}
                onSave={(value) => handleFieldUpdate('geographicRegion', value)}
                disabled={!isOwner}
              />
            </div>
          )}
          {project.tags && (
            <div>
              <h3 className="text-lg font-semibold mb-2">Tags</h3>
              <InlineEditTags
                value={project.tags}
                onSave={(value) => handleFieldUpdate('tags', value)}
                disabled={!isOwner}
              />
            </div>
          )}
        </div>
      ),
    },
    {
      id: 'milestones',
      label: 'Milestones',
      content: (
        <ProjectMilestones
          projectId={project._id || project.id}
          disabled={!isOwner}
          isOwner={isOwner}
        />
      ),
    },
    {
      id: 'goals',
      label: 'Goals',
      content: (
        <ProjectGoals
          projectId={project._id || project.id}
          milestones={project.milestones || []}
          disabled={!isOwner}
          isOwner={isOwner}
        />
      ),
    },
    {
      id: 'voting',
      label: 'Voting',
      content: (
        <VotingTabContent
          project={project}
          isOwner={isOwner}
          projectId={project._id || project.id}
        />
      ),
    },
          {
            id: 'comments',
            label: 'Comments',
            content: (
              <CommentList
                entityType="project"
                entityId={project._id || project.id}
                isProjectOwner={isOwner}
                showRating={true}
              />
            ),
          },
    {
      id: 'chat',
      label: 'Chat',
      content: <ChatRoom projectId={project._id || project.id} />,
    },
    {
      id: 'files',
      label: 'Project Files',
      content: (
        <ProjectFilesList
          files={projectFiles}
          projectId={project._id || project.id}
          isOwner={isOwner}
          onFilesUpdate={async () => {
            if (id) {
              try {
                const files = await api.projectFiles.getProjectFiles(id, false, accessToken || undefined);
                setProjectFiles(files);
              } catch (error) {
                console.error('Error refreshing project files:', error);
              }
            }
          }}
        />
      ),
    },
  ];

  return (
    <div className="container mx-auto px-4 py-8">
      {(images.length > 0 || nearestVotingSession || activeVotingSession) && (
        <div className="mb-6">
          <ProjectHeroCarousel
            items={images.map((img: any) => ({
              fileId: img.id,
              url: img.url,
              caption: img.caption,
            }))}
            nearestVotingSession={nearestVotingSession}
            activeVotingSession={activeVotingSession}
            onVoteClick={handleVoteNowClick}
            isOwner={isOwner}
          />
        </div>
      )}

      <ProjectHeader
        project={project}
        isOwner={isOwner}
        onEdit={() => navigate(`/projects/${project._id || project.id}/edit`)}
        onFieldUpdate={handleFieldUpdate}
      />

      <div className="mt-6" data-tabs-section>
        <ProjectTabs tabs={tabs} defaultTab={activeTab} onTabChange={setActiveTab} />
      </div>
    </div>
  );
};

export default ProjectDetailPage;
